import React, { useEffect, useState } from "react";
import { apiRequest } from "../../../../service/apiRequest";
import { Breadcrumb, Spin, Tag, Select } from "antd";
import { HomeOutlined, CheckCircleFilled } from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";

interface PIDetailItem {
  pid_id: number;
  pid_product_id: number;
  product_name: string;
  total_qty: number;
  ready_qty: number;
}

interface PIDetail {
  pi_id: number;
  pi_order_no: string;
  pi_date: string;
  pi_lead_time: number;
  ledger_company_name: string;
  ledger_first_name: string;
  ledger_last_name: string;
  items: PIDetailItem[];
}

const DailyProgressDetail: React.FC = () => {
  const { pi_id } = useParams<{ pi_id: string }>();
  const [detail, setDetail] = useState<PIDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const [selectedProductIds, setSelectedProductIds] = useState<number[]>([]);

  const handleProductClick = (pidId: number) => {
    navigate(`/workflow/${pi_id}/${pidId}`);
  };

  useEffect(() => {
    if (!pi_id) return;
    (async () => {
      try {
        setLoading(true);
        const data = await apiRequest<PIDetail>({
          method: "get",
          url: `/get-track-pi-detail/${pi_id}`,
        });
        setDetail(data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    })();
  }, [pi_id]);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!detail) {
    return <div style={{ color: "#aaa", padding: 40 }}>PI not found</div>;
  }

  const deadline = dayjs(detail.pi_date).add(detail.pi_lead_time, "day");
  const daysLeft = deadline.diff(dayjs(), "day");
  const borderColor =
    daysLeft < 0 ? "#ff4d4f" : daysLeft <= 3 ? "#faad14" : "#52c41a";
  const tagColor = daysLeft < 0 ? "red" : daysLeft <= 3 ? "orange" : "green";
  const statusText =
    daysLeft < 0
      ? `${Math.abs(daysLeft)} days overdue`
      : daysLeft === 0
        ? "Due today"
        : `${daysLeft} days left`;

  return (
    <div>
      {/* Breadcrumb */}
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item href="/">
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <span
            onClick={() => navigate("/workflow")}
            style={{ cursor: "pointer", color: "#1677ff" }}
          >
            Workflow
          </span>
        </Breadcrumb.Item>
        <Breadcrumb.Item>PI #{detail.pi_order_no}</Breadcrumb.Item>
      </Breadcrumb>

      {/* ── PI Info Card (top) ── */}
      <div
        style={{
          border: `3px solid ${borderColor}`,
          borderRadius: 12,
          padding: "16px 24px",
          background: "#fff",
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 28,
          boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
          width: "100%",
          gap: 16,
        }}
      >
        {/* PI Number */}
        <div style={{ flex: "1 1 120px" }}>
          <div style={{ fontSize: 11, color: "#999", marginBottom: 2 }}>
            PI Number
          </div>
          <div style={{ fontSize: 24, fontWeight: 700, lineHeight: 1 }}>
            #{detail.pi_order_no}
          </div>
          <div style={{ fontSize: 12, color: "#bbb", marginTop: 2 }}>
            PI-{detail.pi_id}
          </div>
        </div>

        <div
          style={{ width: 1, height: 48, background: "#f0f0f0", flexShrink: 0 }}
        />

        {/* Client */}
        <div style={{ flex: "2 1 160px" }}>
          <div style={{ fontSize: 11, color: "#999", marginBottom: 2 }}>
            Client
          </div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>
            {detail.ledger_company_name ||
              `${detail.ledger_first_name} ${detail.ledger_last_name}`}
          </div>
        </div>

        <div
          style={{ width: 1, height: 48, background: "#f0f0f0", flexShrink: 0 }}
        />

        {/* PI Date */}
        <div style={{ flex: "1 1 120px" }}>
          <div style={{ fontSize: 11, color: "#999", marginBottom: 2 }}>
            PI Date
          </div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>
            {dayjs(detail.pi_date).format("DD MMM YYYY")}
          </div>
        </div>

        <div
          style={{ width: 1, height: 48, background: "#f0f0f0", flexShrink: 0 }}
        />

        {/* Deadline */}
        <div style={{ flex: "1 1 140px" }}>
          <div style={{ fontSize: 11, color: "#999", marginBottom: 2 }}>
            Deadline
          </div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>
            {deadline.format("DD MMM YYYY")}
          </div>
          <div style={{ fontSize: 11, color: "#aaa" }}>
            Lead time: {detail.pi_lead_time} days
          </div>
        </div>

        <div
          style={{ width: 1, height: 48, background: "#f0f0f0", flexShrink: 0 }}
        />

        {/* Status */}
        <div style={{ flex: "1 1 100px" }}>
          <div style={{ fontSize: 11, color: "#999", marginBottom: 6 }}>
            Status
          </div>
          <Tag
            color={tagColor}
            style={{ fontWeight: 600, borderRadius: 20, fontSize: 12 }}
          >
            {statusText}
          </Tag>
        </div>
      </div>

      {/* ── Section Title & Filter ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 14,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: "#333",
          }}
        >
          Products ({detail.items.length})
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13, color: "#666" }}>Filter Products:</span>
          <Select
            mode="multiple"
            placeholder="All Products"
            style={{ minWidth: 240 }}
            allowClear
            value={selectedProductIds}
            onChange={(vals) => setSelectedProductIds(vals)}
            options={detail.items.map(item => ({ label: item.product_name, value: item.pid_id }))}
          />
        </div>
      </div>

      {/* ── Product Cards ── */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
        {detail.items.length === 0 && (
          <div style={{ color: "#aaa" }}>No products found</div>
        )}

        {detail.items
          .filter(item => selectedProductIds.length === 0 || selectedProductIds.includes(item.pid_id))
          .map((item) => {
            const isDone =
              item.total_qty > 0 &&
              Number(item.ready_qty) >= Number(item.total_qty);
            const percent =
              item.total_qty > 0
                ? Math.min(
                  Math.round(
                    (Number(item.ready_qty) / Number(item.total_qty)) * 100,
                  ),
                  100,
                )
                : 0;

            return (
              <div
                key={item.pid_id}
                onClick={() => handleProductClick(item.pid_id)}
                style={{
                  width: 240,
                  border: `2px solid ${isDone ? "#52c41a" : "#e8e8e8"}`,
                  borderRadius: 12,
                  padding: "16px 18px",
                  background: "#fff",
                  cursor: "pointer",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                  transition: "transform 0.15s, box-shadow 0.15s",
                  position: "relative",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.transform =
                    "translateY(-3px)";
                  (e.currentTarget as HTMLDivElement).style.boxShadow =
                    "0 6px 16px rgba(0,0,0,0.12)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.transform =
                    "translateY(0)";
                  (e.currentTarget as HTMLDivElement).style.boxShadow =
                    "0 2px 8px rgba(0,0,0,0.06)";
                }}
              >
                {/* Green tick */}
                {isDone && (
                  <CheckCircleFilled
                    style={{
                      position: "absolute",
                      top: 12,
                      right: 12,
                      color: "#52c41a",
                      fontSize: 18,
                    }}
                  />
                )}

                {/* Product Name */}
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#1a1a1a",
                    marginBottom: 12,
                    paddingRight: isDone ? 26 : 0,
                    lineHeight: 1.4,
                  }}
                >
                  {item.product_name}
                </div>

                <div
                  style={{ borderTop: "1px solid #f5f5f5", marginBottom: 10 }}
                />

                {/* Total QTY */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 6,
                  }}
                >
                  <span style={{ fontSize: 12, color: "#888" }}>Total QTY</span>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>
                    {item.total_qty}
                  </span>
                </div>

                {/* Ready QTY */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 14,
                  }}
                >
                  <span style={{ fontSize: 12, color: "#888" }}>Ready QTY</span>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: isDone
                        ? "#52c41a"
                        : Number(item.ready_qty) > 0
                          ? "#1677ff"
                          : "#bbb",
                    }}
                  >
                    {item.ready_qty}
                  </span>
                </div>

                {/* Progress Bar */}
                <div
                  style={{
                    background: "#f0f0f0",
                    borderRadius: 10,
                    height: 6,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${percent}%`,
                      height: "100%",
                      background: isDone ? "#52c41a" : "#1677ff",
                      borderRadius: 10,
                      transition: "width 0.4s ease",
                    }}
                  />
                </div>
                <div
                  style={{
                    textAlign: "right",
                    fontSize: 11,
                    color: "#bbb",
                    marginTop: 4,
                  }}
                >
                  {percent}%
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default DailyProgressDetail;
